import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { AddonGroupInterface } from '../types';

interface AddonGroupsDisplayProps {
	addonGroups: AddonGroupInterface[];
	onAddGroup: () => void;
	onEditGroup: (group: AddonGroupInterface) => void;
	currency: string | undefined;
}

const AddonGroupsDisplay: React.FC<AddonGroupsDisplayProps> = ({ addonGroups, onAddGroup, onEditGroup, currency }) => {
	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h6">Addon Groups</Typography>
				<Button startIcon={<AddIcon />} onClick={onAddGroup} variant="outlined" size="small">
					Add Group
				</Button>
			</Box>

			{addonGroups.length === 0 ? (
				<Box
					sx={{
						textAlign: 'center',
						py: 4,
						border: '1px dashed #ccc',
						borderRadius: 1,
					}}
				>
					<Typography variant="body2" color="text.secondary">
						No addon groups yet. Click "Add Group" to create one.
					</Typography>
				</Box>
			) : (
				<Box sx={{ display: 'grid', gridGap: '16px' }}>
					{addonGroups.map((group) => (
						<Box
							key={group.id}
							sx={{
								p: 2,
								borderRadius: 1,
								border: '1px solid',
								borderColor: 'divider',
								bgcolor: 'background.paper',
							}}
						>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body1" sx={{ fontWeight: 600 }}>
										{group.name}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
									{group.is_required && (
										<div className="px-2 py-1 rounded-md text-xs bg-red-100 text-red-800">
											Required
										</div>
									)}
									<div className="px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
										Select {group.min_addons_select || 0}-{group.max_addons_select || 1}
									</div>
									<IconButton size="small" onClick={() => onEditGroup(group)} sx={{ ml: 1 }}>
										<EditIcon fontSize="small" />
									</IconButton>
								</Box>
							</Box>

							{group.addons && group.addons.length > 0 && (
								<Box sx={{ display: 'grid', gridGap: '8px', mt: 1 }}>
									{group.addons.map((addon) => (
										<Box
											key={addon.id}
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												p: 1,
												borderRadius: 1,
												bgcolor: 'grey.50',
											}}
										>
											<Box sx={{ flex: 1 }}>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													{addon.name}
													{addon.is_out_of_stock && (
														<span className="ml-2 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
															Out of Stock
														</span>
													)}
													{addon.is_multi && (
														<span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
															Multi (max {addon.quantity})
														</span>
													)}
												</Typography>
											</Box>
											<Typography
												variant="body2"
												sx={{
													fontWeight: 600,
													color: (addon.price || 0) > 0 ? 'primary.main' : 'text.secondary',
													minWidth: '60px',
													textAlign: 'right',
												}}
											>
												{(addon.price || 0) > 0
													? `+${currency}${Number(addon.price || 0).toFixed(2)}`
													: 'Free'}
											</Typography>
										</Box>
									))}
								</Box>
							)}

							{(!group.addons || group.addons.length === 0) && (
								<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
									No addons in this group
								</Typography>
							)}
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default AddonGroupsDisplay;
